import React from "react";
import { Paper, Stack, Typography, Switch, useTheme, alpha, Box, Chip, IconButton, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import PaletteIcon from "@mui/icons-material/Palette";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { colorByLevel } from "@/constants";
import { useSelector } from "react-redux";
import { IApplicationState } from "@/store-redux";

export type PracticeTabMode = "pronunciation" | "main_idea";

interface ToolbarSectionProps {
  activeMode: PracticeTabMode;
  onChangeMode: (mode: PracticeTabMode) => void;
  showWordTooltip: boolean;
  showVocaLevel: boolean;
  onTooltipToggle: (enabled: boolean) => void;
  onVocaLevelToggle: (enabled: boolean) => void;
  onOpenShadowingGuide: () => void;
}

const ToolbarSection: React.FC<ToolbarSectionProps> = ({
  activeMode,
  onChangeMode,
  showWordTooltip,
  showVocaLevel,
  onTooltipToggle,
  onVocaLevelToggle,
  onOpenShadowingGuide,
}) => {
  const theme = useTheme();
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        p: { xs: 2, md: 2.5 },
        background: isDark ? alpha(theme.palette.background.paper, 0.5) : alpha("#ffffff", 0.9),
        backdropFilter: "blur(20px)",
        border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(102, 126, 234, 0.15)",
        boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.25)" : "0 8px 24px rgba(102, 126, 234, 0.08)",
      }}
    >
      <Stack spacing={2}>
        {/* Main Title Header */}
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(102, 126, 234, 0.3)",
              }}
            >
              <PsychologyIcon sx={{ color: "white", fontSize: 19 }} />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: "17px",
                }}
              >
                Chế độ luyện tập
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, display: "block" }}>
                Chọn 1 trong 2 phần để tập trung luyện tập hiệu quả
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 2 Main Option Cards (Single Selection Choice) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          {/* OPTION 1: Phát âm & Shadowing */}
          <Paper
            elevation={0}
            onClick={() => onChangeMode("pronunciation")}
            sx={{
              p: 2,
              borderRadius: "18px",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              background:
                activeMode === "pronunciation"
                  ? isDark
                    ? "linear-gradient(135deg, rgba(39, 174, 96, 0.2) 0%, rgba(39, 174, 96, 0.08) 100%)"
                    : "linear-gradient(135deg, rgba(39, 174, 96, 0.12) 0%, rgba(39, 174, 96, 0.03) 100%)"
                  : isDark
                    ? alpha("#fff", 0.02)
                    : "#fafafa",
              border:
                activeMode === "pronunciation"
                  ? `2px solid #27ae60`
                  : `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
              boxShadow: activeMode === "pronunciation" ? `0 6px 20px ${alpha("#27ae60", isDark ? 0.3 : 0.2)}` : "none",
              transform: activeMode === "pronunciation" ? "translateY(-2px)" : "none",
              "&:hover": {
                borderColor: "#27ae60",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Stack spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      bgcolor: activeMode === "pronunciation" ? "#27ae60" : alpha("#27ae60", 0.15),
                      color: activeMode === "pronunciation" ? "#fff" : "#27ae60",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <RecordVoiceOverIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          color: activeMode === "pronunciation" ? (isDark ? "#4ade80" : "#1e7e34") : "text.primary",
                          fontSize: "14px",
                        }}
                      >
                        Phần 1: Luyện phát âm & Shadowing
                      </Typography>
                      <Tooltip title="Xem hướng dẫn phương pháp Shadowing" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenShadowingGuide();
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            color: activeMode === "pronunciation" ? "#27ae60" : "text.secondary",
                            bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(39, 174, 96, 0.08)",
                            "&:hover": {
                              bgcolor: "#27ae60",
                              color: "#fff",
                            },
                          }}
                        >
                          <HelpOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "11px" }}>
                      Ghi âm chấm điểm phát âm & Luyện nói shadowing theo câu
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  label={activeMode === "pronunciation" ? "Đang chọn" : "Chọn"}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "11px",
                    bgcolor: activeMode === "pronunciation" ? "#27ae60" : "transparent",
                    color: activeMode === "pronunciation" ? "#fff" : "text.secondary",
                    border: `1px solid ${activeMode === "pronunciation" ? "#27ae60" : isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
                  }}
                />
              </Box>
            </Stack>
          </Paper>

          {/* OPTION 2: Học ý chính & Từ khóa */}
          <Paper
            elevation={0}
            onClick={() => onChangeMode("main_idea")}
            sx={{
              p: 2,
              borderRadius: "18px",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              background:
                activeMode === "main_idea"
                  ? isDark
                    ? "linear-gradient(135deg, rgba(142, 68, 173, 0.2) 0%, rgba(142, 68, 173, 0.08) 100%)"
                    : "linear-gradient(135deg, rgba(142, 68, 173, 0.12) 0%, rgba(142, 68, 173, 0.03) 100%)"
                  : isDark
                    ? alpha("#fff", 0.02)
                    : "#fafafa",
              border:
                activeMode === "main_idea"
                  ? `2px solid #8e44ad`
                  : `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
              boxShadow: activeMode === "main_idea" ? `0 6px 20px ${alpha("#8e44ad", isDark ? 0.3 : 0.2)}` : "none",
              transform: activeMode === "main_idea" ? "translateY(-2px)" : "none",
              "&:hover": {
                borderColor: "#8e44ad",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Stack spacing={1.2}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      bgcolor: activeMode === "main_idea" ? "#8e44ad" : alpha("#8e44ad", 0.15),
                      color: activeMode === "main_idea" ? "#fff" : "#8e44ad",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <AutoStoriesIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 800,
                        color: activeMode === "main_idea" ? (isDark ? "#c084fc" : "#7b1fa2") : "text.primary",
                        fontSize: "14px",
                      }}
                    >
                      Phần 2: Học ý chính & Từ khóa
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "11px" }}>
                      Bóc tách ý chính, từ khóa, tra từ điển & tô màu CEFR
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  label={activeMode === "main_idea" ? "Đang chọn" : "Chọn"}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "11px",
                    bgcolor: activeMode === "main_idea" ? "#8e44ad" : "transparent",
                    color: activeMode === "main_idea" ? "#fff" : "text.secondary",
                    border: `1px solid ${activeMode === "main_idea" ? "#8e44ad" : isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
                  }}
                />
              </Box>

              {/* In-card controls when active */}
              {activeMode === "main_idea" && (
                <Box
                  sx={{
                    pt: 1,
                    borderTop: `1px dashed ${isDark ? "rgba(255,255,255,0.1)" : "rgba(142, 68, 173, 0.2)"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Tooltip Toggle */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <InfoIcon sx={{ fontSize: 16, color: "#8e44ad" }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? "#fff" : "#1e293b" }}>
                      Chú thích từ điển:
                    </Typography>
                    <Switch
                      checked={showWordTooltip}
                      onChange={(e) => onTooltipToggle(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#8e44ad" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#8e44ad" },
                      }}
                    />
                  </Stack>

                  {/* Level Toggle & CEFR Pills */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PaletteIcon sx={{ fontSize: 16, color: "#e74c3c" }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? "#fff" : "#1e293b" }}>
                      Cấp độ CEFR:
                    </Typography>
                    <Switch
                      checked={showVocaLevel}
                      onChange={(e) => onVocaLevelToggle(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#e74c3c" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#e74c3c" },
                      }}
                    />
                    <Stack direction="row" spacing={0.3} sx={{ opacity: showVocaLevel ? 1 : 0.4 }}>
                      {["A1", "A2", "B1", "B2", "C1", "C2"].map((level) => (
                        <Box
                          key={level}
                          sx={{
                            color: "white",
                            bgcolor: colorByLevel[level as keyof typeof colorByLevel] || "#ccc",
                            fontWeight: 700,
                            fontSize: "8px",
                            px: 0.5,
                            py: 0.1,
                            borderRadius: "3px",
                          }}
                        >
                          {level}
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Paper>
  );
};

export default React.memo(ToolbarSection);
